// EFM party roles. Used as the OFFLINE FALLBACK for the wizard's role dropdown.
// The live wizard hydrates from policy via /efiling/reference/party-roles
// (118 codes for Madera as of 2026-04-17), and isRoleSelfRepEligible() below
// is the seed-derived gate for whether a self-rep checkbox renders. So when
// adding a new self-rep-friendly role, it must appear here AND have
// selfRepVisible: true to actually surface in the wizard.
export const PARTY_ROLES = [
    // ── Civil — original seed (cert-sample lineage) ───────────────────────────
    { code: 'ARRCLM', name: 'Arrieta Claimant', selfRepVisible: false },
    { code: 'ASSIGN', name: 'Assignee', selfRepVisible: false },
    { code: 'ATT', name: 'Attorney', selfRepVisible: false },
    { code: 'CLAIM', name: 'Claimant', selfRepVisible: true },
    { code: 'XCMPLNT', name: 'Cross-Complainant', selfRepVisible: true },
    { code: 'INTXC', name: 'Cross-Complainant in Intervention', selfRepVisible: false },
    { code: 'XDEF', name: 'Cross-Defendant', selfRepVisible: false },
    { code: 'INTXD', name: 'Cross-Defendant in Intervention', selfRepVisible: false },
    { code: 'XPLAIN', name: 'Cross-Plaintiff', selfRepVisible: true },
    { code: 'COR', name: 'Custodian of Records', selfRepVisible: false },
    { code: 'DEF', name: 'Defendant', selfRepVisible: false },
    { code: 'INTPDEF', name: 'Defendant in Interpleader', selfRepVisible: false },
    { code: 'INTDEF', name: 'Defendant in Intervention', selfRepVisible: false },
    { code: 'INTPLD', name: 'Interpleader', selfRepVisible: false },
    { code: 'LEG', name: 'Legacy Party', selfRepVisible: false },
    { code: 'TPTY', name: 'Non-Party', selfRepVisible: false },
    { code: 'PET', name: 'Petitioner', selfRepVisible: true },
    { code: 'PLAIN', name: 'Plaintiff', selfRepVisible: true },
    { code: 'INTPLAIN', name: 'Plaintiff in Interpleader', selfRepVisible: true },
    { code: 'INTVEN', name: 'Plaintiff in Intervention', selfRepVisible: true },
    { code: 'REALPARTYINT', name: 'Real Party in Interest', selfRepVisible: false },
    { code: 'RES', name: 'Respondent', selfRepVisible: true },
    { code: 'SUB', name: 'Submitted By', selfRepVisible: false },
    { code: 'WIT', name: 'Witness', selfRepVisible: false },
    // ── Live Madera additions (PARTY_TYPE policy, 2026-04-17 sync) ────────────
    // Probate — conservatorship / guardianship / trust / estate
    { code: 'CONTE', name: 'Conservatee', selfRepVisible: false },
    { code: 'CONTR', name: 'Conservator', selfRepVisible: true },
    { code: 'CONTRP', name: 'Previous Conservator', selfRepVisible: false },
    { code: 'SCONS', name: 'Successor Conservator', selfRepVisible: true },
    { code: 'GRD', name: 'Guardian', selfRepVisible: true },
    { code: 'GRDAD', name: 'Guardian Ad Litem', selfRepVisible: false },
    { code: 'GRDP', name: 'Previous Guardian', selfRepVisible: false },
    { code: 'GDRL', name: 'Guardian-Licensed', selfRepVisible: false },
    { code: 'PROPGRD', name: 'Proposed Guardian', selfRepVisible: true },
    { code: 'PGRD', name: 'Public Guardian', selfRepVisible: false },
    { code: 'TRUST', name: 'Trustee', selfRepVisible: true },
    { code: 'TRUSTR', name: 'Trustor', selfRepVisible: true },
    { code: 'TRUSTP', name: 'Previous Trustee', selfRepVisible: false },
    { code: 'DEC', name: 'Decedent', selfRepVisible: false },
    { code: 'ADMIN', name: 'Administrator', selfRepVisible: true },
    { code: 'ADMINP', name: 'Previous Administrator', selfRepVisible: false },
    { code: 'ADMINS', name: 'Successor Administrator', selfRepVisible: true },
    { code: 'EXEC', name: 'Executor', selfRepVisible: true },
    { code: 'EXECP', name: 'Previous Executor', selfRepVisible: false },
    { code: 'SPADM', name: 'Special Administrator', selfRepVisible: true },
    { code: 'PADMIN', name: 'Public Administrator', selfRepVisible: false },
    { code: 'BENE', name: 'Beneficiary', selfRepVisible: true },
    { code: 'PRSREP', name: 'Personal Representative', selfRepVisible: true },
    { code: 'PTM', name: 'Petitioner/Minor', selfRepVisible: false },
    { code: 'CRED', name: 'Creditor', selfRepVisible: true },
    { code: 'CTINV', name: 'Court Investigator', selfRepVisible: false },
    { code: 'OBJ', name: 'Objector', selfRepVisible: true },
    { code: 'CONTS', name: 'Contestant', selfRepVisible: true },
    { code: 'WLTEST', name: 'Testator', selfRepVisible: false },
    { code: 'LODGE', name: 'Person Lodging Will', selfRepVisible: true },
    // Family — DV, dissolution, parentage, child support
    { code: 'BRIDE', name: 'Bride', selfRepVisible: true },
    { code: 'GROOM', name: 'Groom', selfRepVisible: true },
    { code: 'PETPR', name: 'Pet/Person to be Restrained', selfRepVisible: true },
    { code: 'PETPSO', name: 'Pet/Person Seeking Order', selfRepVisible: true },
    { code: 'RESPR', name: 'Res/Person to be Restrained', selfRepVisible: true },
    { code: 'RESPSO', name: 'Res/Person Seeking Order', selfRepVisible: true },
    { code: 'MOTH', name: 'Mother', selfRepVisible: true },
    { code: 'FATH', name: 'Father', selfRepVisible: true },
    { code: 'PRESF', name: 'Presumed Father', selfRepVisible: false },
    { code: 'ALLF', name: 'Alleged Father', selfRepVisible: false },
    { code: 'FATHA', name: 'Alleged Father', selfRepVisible: false },
    { code: 'FATHBI', name: 'Biological Father', selfRepVisible: false },
    { code: 'OTHP', name: 'Other Parent', selfRepVisible: true },
    { code: 'OTHPF', name: 'Other Parent-Father', selfRepVisible: true },
    { code: 'OTHPM', name: 'Other Parent-Mother', selfRepVisible: true },
    { code: 'NATPAR', name: 'Natural Parents', selfRepVisible: false },
    { code: 'PARENT', name: 'Parent', selfRepVisible: true },
    { code: 'PARDF', name: 'De-Facto Parent', selfRepVisible: true },
    { code: 'CHILD', name: 'Child', selfRepVisible: false },
    { code: 'MINOR', name: 'Minor', selfRepVisible: false },
    { code: 'ADOPTM', name: 'Minor Adoptee', selfRepVisible: false },
    { code: 'ADOPT', name: 'Adult Adoptee', selfRepVisible: true },
    { code: 'CUST', name: 'Custodian of Minor', selfRepVisible: false },
    { code: 'OBLGE', name: 'Obligee', selfRepVisible: true },
    { code: 'OBLGR', name: 'Obligor', selfRepVisible: true },
    { code: 'DCSS', name: 'DCSS', selfRepVisible: false },
    // Civil — live-policy codes that drift from legacy seed names (kept BOTH
    // for compatibility; the live one is what JTI returns from policy hydration).
    { code: 'ATTY', name: 'Attorney', selfRepVisible: false }, // alias of ATT
    { code: 'CLMT', name: 'Claimant', selfRepVisible: true }, // alias of CLAIM
    { code: 'COMPX', name: 'Cross Complainant', selfRepVisible: true }, // live form of XCMPLNT
    { code: 'DEFX', name: 'Cross Defendant', selfRepVisible: false }, // live form of XDEF
    { code: 'INTP', name: 'Interpleader', selfRepVisible: false }, // live form of INTPLD
    { code: 'INTV', name: 'Intervenor', selfRepVisible: true },
    { code: 'RPI', name: 'Real Party in Interest', selfRepVisible: false }, // live form of REALPARTYINT
    { code: 'WITNSS', name: 'Witness', selfRepVisible: false }, // live form of WIT
    { code: 'APLNT', name: 'Appellant', selfRepVisible: true },
    { code: 'APP', name: 'Applicant', selfRepVisible: true },
    { code: 'JOIN', name: 'Joinder', selfRepVisible: false },
    { code: 'MP', name: 'Moving Party', selfRepVisible: true },
    { code: 'RP', name: 'Responding Party', selfRepVisible: true },
    // Specialty roles surfaced by Madera policy
    { code: 'AGENT', name: 'Agent', selfRepVisible: false },
    { code: 'AGENCY', name: 'Agency', selfRepVisible: false },
    { code: 'AFS', name: 'Agent for Service', selfRepVisible: false },
    { code: 'REGA', name: 'Registered Agent', selfRepVisible: false },
    { code: 'AG', name: 'Attorney General', selfRepVisible: false },
    { code: 'CA', name: 'City Attorney', selfRepVisible: false },
    { code: 'DA', name: 'District Attorney', selfRepVisible: false },
    { code: 'STATE', name: 'State', selfRepVisible: false },
    { code: 'COURT', name: 'Court', selfRepVisible: false },
    { code: 'CTREP', name: 'Court Reporter', selfRepVisible: false },
    { code: 'JUROR', name: 'Juror', selfRepVisible: false },
    { code: 'BOND', name: 'Bondsman or Company', selfRepVisible: false },
    { code: 'BPAY', name: 'Bail Payor', selfRepVisible: true },
    { code: 'PAY', name: 'Payor', selfRepVisible: true },
    { code: 'OTH', name: 'Other', selfRepVisible: false },
    { code: 'VICTIM', name: 'Victim', selfRepVisible: true },
    { code: 'PAT', name: 'Patient', selfRepVisible: false },
    { code: 'CNPAR', name: 'Counsel for Parent', selfRepVisible: false },
];
export const SELF_REP_ELIGIBLE_ROLES = PARTY_ROLES
    .filter(r => r.selfRepVisible)
    .map(r => r.code);
/**
 * Defendant-side role codes (Walter R103 — the unlawful-detainer
 * defendant-address rule keys on these). DEFX is the live-form duplicate of
 * XDEF; both are kept so the rule holds whichever code the policy served.
 */
export const DEFENDANT_ROLE_CODES = ['DEF', 'XDEF', 'DEFX', 'INTPDEF', 'INTDEF'];
export const isDefendantRole = (roleCode) => DEFENDANT_ROLE_CODES.includes(roleCode);
export const getPartyRoles = () => PARTY_ROLES;
export const isRoleSelfRepEligible = (roleCode) => SELF_REP_ELIGIBLE_ROLES.includes(roleCode);
